import React from 'react';
import type { Project } from '../services/projectService';
import type { RequirementWithDetails } from '../services/requirementService';

interface ScheduleHealthCardProps {
  project: Project;
  requirements: RequirementWithDetails[];
}

interface ScheduleHealth {
  daysRemaining: number | null;
  expectedProgress: number | null;
  actualProgress: number;
  variance: number | null;
  health: 'ahead' | 'on-track' | 'behind' | 'unknown';
  totalDays: number | null;
  daysElapsed: number | null;
}

export function ScheduleHealthCard({ project, requirements }: ScheduleHealthCardProps): React.ReactElement {
  const calculateScheduleHealth = (): ScheduleHealth => {
    if (!project.target_completion_date) {
      return {
        daysRemaining: null,
        expectedProgress: null,
        actualProgress: 0,
        variance: null,
        health: 'unknown',
        totalDays: null,
        daysElapsed: null
      };
    }

    const today = new Date();
    const target = new Date(project.target_completion_date);
    const daysRemaining = Math.ceil((target.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));

    // Calculate expected progress based on dates
    const start = project.start_date ? new Date(project.start_date) : null;
    const totalDays = start ? Math.ceil((target.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) : null;
    const daysElapsed = start ? Math.ceil((today.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) : null;

    const expectedProgress = totalDays && daysElapsed ? (daysElapsed / totalDays) * 100 : null;

    // Calculate actual progress from requirements
    const statusCounts = requirements.reduce((acc, req) => {
      acc[req.status] = (acc[req.status] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    const totalActive = (statusCounts.approved || 0) + (statusCounts['in-progress'] || 0) + (statusCounts.completed || 0);
    const actualProgress = totalActive > 0 ? ((statusCounts.completed || 0) / totalActive) * 100 : 0;

    const variance = expectedProgress !== null ? actualProgress - expectedProgress : null;

    const health = variance !== null
      ? variance > 10 ? 'ahead'
      : variance < -10 ? 'behind'
      : 'on-track'
      : 'unknown';

    return {
      daysRemaining,
      expectedProgress,
      actualProgress,
      variance,
      health,
      totalDays,
      daysElapsed
    };
  };

  const scheduleHealth = calculateScheduleHealth();

  // Format date for display
  const formatDate = (dateString?: string | null): string => {
    if (!dateString) return 'Not set';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  // Determine if overdue
  const isOverdue = scheduleHealth.daysRemaining !== null && scheduleHealth.daysRemaining < 0;

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-50 dark:text-gray-50">Schedule Health</h3>
        {scheduleHealth.health === 'ahead' && (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 border border-green-200 dark:border-green-800">
            <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            Ahead of Schedule
          </span>
        )}
        {scheduleHealth.health === 'on-track' && (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 border border-blue-200 dark:border-blue-800">
            <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
            </svg>
            On Track
          </span>
        )}
        {scheduleHealth.health === 'behind' && (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300 border border-red-200 dark:border-red-800">
            <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            Behind Schedule
          </span>
        )}
        {scheduleHealth.health === 'unknown' && (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300 border border-gray-200 dark:border-gray-700 dark:border-gray-600">
            <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
            </svg>
            No Target Date
          </span>
        )}
      </div>

      {scheduleHealth.daysRemaining !== null ? (
        <>
          {/* Days Remaining & Variance */}
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div className={`p-4 rounded-lg border ${
              isOverdue
                ? 'bg-red-50 border-red-200'
                : scheduleHealth.daysRemaining <= 7
                  ? 'bg-yellow-50 border-yellow-200'
                  : 'bg-blue-50 border-blue-200'
            }`}>
              <div className="text-xs text-gray-600 dark:text-gray-400 dark:text-gray-600 mb-1 font-medium">
                {isOverdue ? 'Days Overdue' : 'Days Remaining'}
              </div>
              <div className={`text-3xl font-bold ${
                isOverdue
                  ? 'text-red-700 dark:text-red-400'
                  : scheduleHealth.daysRemaining <= 7
                    ? 'text-yellow-700 dark:text-yellow-400'
                    : 'text-blue-700 dark:text-blue-400'
              }`}>
                {Math.abs(scheduleHealth.daysRemaining)}
              </div>
            </div>

            {scheduleHealth.variance !== null && (
              <div className={`p-4 rounded-lg border ${
                scheduleHealth.variance > 0
                  ? 'bg-green-50 border-green-200'
                  : scheduleHealth.variance < 0
                    ? 'bg-red-50 border-red-200'
                    : 'bg-blue-50 border-blue-200'
              }`}>
                <div className="text-xs text-gray-600 dark:text-gray-400 dark:text-gray-600 mb-1 font-medium">Schedule Variance</div>
                <div className={`text-3xl font-bold ${
                  scheduleHealth.variance > 0
                    ? 'text-green-700 dark:text-green-400'
                    : scheduleHealth.variance < 0
                      ? 'text-red-700 dark:text-red-400'
                      : 'text-blue-700 dark:text-blue-400'
                }`}>
                  {scheduleHealth.variance > 0 ? '+' : ''}{scheduleHealth.variance.toFixed(0)}%
                </div>
              </div>
            )}
          </div>

          {/* Progress Comparison */}
          {scheduleHealth.expectedProgress !== null && (
            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 border border-gray-200 dark:border-gray-700 dark:border-gray-600">
              <div className="text-xs text-gray-600 dark:text-gray-400 dark:text-gray-600 mb-3 font-medium">Progress Analysis</div>
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-700 dark:text-gray-300">Expected Progress:</span>
                  <span className="text-sm font-semibold text-gray-900 dark:text-gray-50">
                    {scheduleHealth.expectedProgress.toFixed(0)}%
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-700 dark:text-gray-300">Actual Progress:</span>
                  <span className="text-sm font-semibold text-gray-900 dark:text-gray-50">
                    {scheduleHealth.actualProgress.toFixed(0)}%
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Date Information */}
          <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-500 dark:text-gray-400 dark:text-gray-600">Start Date:</span>
                <div className="font-medium text-gray-900 dark:text-gray-50">{formatDate(project.start_date)}</div>
              </div>
              <div>
                <span className="text-gray-500 dark:text-gray-400 dark:text-gray-600">Target Date:</span>
                <div className="font-medium text-gray-900 dark:text-gray-50">{formatDate(project.target_completion_date)}</div>
              </div>
            </div>
            {scheduleHealth.totalDays && (
              <div className="mt-2 text-xs text-gray-500 dark:text-gray-400 dark:text-gray-600">
                {scheduleHealth.daysElapsed} of {scheduleHealth.totalDays} days elapsed
              </div>
            )}
          </div>
        </>
      ) : (
        <div className="text-center py-8">
          <svg
            className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-600"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
            />
          </svg>
          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400 dark:text-gray-600">
            Set a target completion date to track schedule health
          </p>
        </div>
      )}
    </div>
  );
}
